/**
 * One game's page: the step between the portal and the table.
 *
 * A visitor arrives knowing nothing and leaves having chosen a way in. The
 * three ways in are the modes the game already has, described by what they
 * give you rather than by what they are called, with Belajar first because
 * that is the honest recommendation for someone who has never played.
 *
 * The page is driven by the catalog entry, so a second game gets this screen
 * for free.
 */

import { getCategory, getGame } from "@kikik/game-catalog";
import { useI18n } from "../i18n";
import type { AppMode } from "../components";
import { Motif } from "./art/Motif";
import { PortalFooter } from "./PortalFooter";
import { PortalHeader } from "./PortalHeader";
import type { NavTarget } from "./PortalHeader";
import { Stamp } from "./Stamp";
import { TileStage } from "./art/TileStage";
import { useReveal } from "./useReveal";

const WAYS: readonly { mode: AppMode; step: "learn" | "solo" | "online" }[] = [
  { mode: "belajar", step: "learn" },
  { mode: "solo", step: "solo" },
  { mode: "online", step: "online" },
];

export interface GameEntryProps {
  slug: string;
  onHome: () => void;
  onNavigateHome: (target: NavTarget) => void;
  onPlay: (mode: AppMode) => void;
  onOpenGame: (slug: string) => void;
}

export function GameEntry({ slug, onHome, onNavigateHome, onPlay, onOpenGame }: GameEntryProps) {
  const { t } = useI18n();
  const game = getGame(slug);
  useReveal(true);

  if (!game) {
    return (
      <div className="portal" data-screen="missing">
        <PortalHeader
          onHome={onHome}
          onNavigate={onNavigateHome}
          onOpenGame={onOpenGame}
          onOpenCategory={() => onNavigateHome("categories")}
          onOpenMood={() => onNavigateHome("games")}
        />
        <main id="main" className="shell">
          <p className="empty" role="status">
            {t("portal.search.empty", { query: slug })}
          </p>
          <button type="button" className="btn btn--primary" onClick={onHome}>
            {t("portal.entry.back")}
          </button>
        </main>
        <PortalFooter onNavigate={onNavigateHome} />
      </div>
    );
  }

  const category = getCategory(game.category);

  return (
    <div className="portal" data-screen="game">
      <a className="skiplink" href="#main">
        {t("portal.skipToContent")}
      </a>
      <PortalHeader
        current="games"
        onHome={onHome}
        onNavigate={onNavigateHome}
        onOpenGame={onOpenGame}
        onOpenCategory={() => onNavigateHome("categories")}
        onOpenMood={() => onNavigateHome("games")}
      />

      <main id="main">
        <section className="entry" aria-labelledby="entry-title">
          <div className="shell entry__inner">
            <div className="entry__copy">
              <nav className="crumbs" aria-label={t("portal.nav.label")}>
                <button type="button" className="crumbs__home" onClick={onHome}>
                  <span aria-hidden="true">←</span> {t("portal.entry.back")}
                </button>
              </nav>

              <p className="eyebrow eyebrow--onjade">
                {category ? t(category.nameKey) : ""} · {t("portal.status.playable")}
              </p>
              <h1 className="entry__title" id="entry-title">
                {t(game.titleKey)}
              </h1>
              <p className="entry__tagline">{t(game.taglineKey)}</p>
              <p className="entry__description">{t(game.descriptionKey)}</p>

              <div className="entry__meta">
                <Stamp status={game.status} />
                <p className="entry__platforms">
                  <span className="entry__platform-label">{t("portal.platform.label")}</span>
                  {game.platforms.map((platform) => (
                    <span key={platform} className="tag tag--onjade">
                      {t(`portal.platform.${platform}`)}
                    </span>
                  ))}
                </p>
              </div>
            </div>

            <div className="entry__stage">
              <TileStage label={t("portal.hero.artLabel")} />
            </div>
          </div>
        </section>

        <section className="section section--ways" aria-labelledby="ways-title" data-reveal="out">
          <div className="shell">
            <h2 className="section__title" id="ways-title">
              {t("portal.entry.choose")}
            </h2>
            <p className="section__lead">{t("portal.entry.chooseLead")}</p>

            <ol className="ways">
              {WAYS.map((way, index) => (
                <li key={way.mode} className="way" data-step={way.step}>
                  <span className="way__index" aria-hidden="true">
                    {index + 1}
                  </span>
                  <Motif
                    motif={way.step === "learn" ? "flask" : way.step === "solo" ? "board" : "confetti"}
                    className="way__art"
                  />
                  <h3 className="way__name">{t(`portal.ladder.${way.step}.name`)}</h3>
                  <p className="way__desc">{t(`portal.ladder.${way.step}.desc`)}</p>
                  <button
                    type="button"
                    className="btn btn--primary way__cta"
                    data-play={way.mode}
                    onClick={() => onPlay(way.mode)}
                  >
                    {t("portal.entry.open")}
                  </button>
                </li>
              ))}
            </ol>

            <div className="rules">
              <h3 className="rules__title">{t("portal.entry.rules.title")}</h3>
              <p className="rules__body">{t("portal.entry.rules.body")}</p>
            </div>
          </div>
        </section>
      </main>

      <PortalFooter onNavigate={onNavigateHome} />
    </div>
  );
}
